import { createStore } from '@/lib/storeCreator';
import { sitesApi } from '@/features/sites/services';
import { getErrorMessage } from '@/types/api';
import {
  createSiteFieldUpdate,
  isUpdatableField,
  validateFieldValue,
  type UpdatableSiteField,
  type SiteFieldValue,
} from '@/utils/typeSafeUpdates';
import type {
  Site,
  SiteCreation,
  SiteChanges,
  Material,
  SiteMaterial,
  Photo,
  ChangedSiteMaterials,
  NewPhotoData,
  NewPhotosData,
  SiteDetails,
  SitePhoto,
  PhotoUpload,
  UpdateStatus,
} from '@/features/sites/types';

interface SitesStoreState {
  // Sites data
  sites: Site[];
  currentSite: Site | null;
  currentSiteDetails: SiteDetails | null;
  updateStatuses: Record<string, UpdateStatus>;

  // Materials data
  materials: Material[];
  siteMaterials: SiteMaterial[];
  availableMaterials: SiteMaterial[];

  // Photos data
  photos: Photo[];
  sitePhotos: SitePhoto[];

  // Unified loading states using state machine pattern
  loading: {
    sites: 'idle' | 'fetching' | 'creating' | 'updating' | 'deleting' | 'error';
    materials: 'idle' | 'fetching' | 'updating' | 'deleting' | 'error';
    photos: 'idle' | 'fetching' | 'uploading' | 'deleting' | 'error';
  };

  // Error states
  sitesError: string | null;
  siteError: string | null;
  materialsError: string | null;
  photosError: string | null;
}

export type ActionResult<T = any> = {
  success: boolean;
  data?: T;
  error?: string;
};

interface SitesStoreActions {
  // Sites actions
  getSites: (projectId: number) => Promise<void>;
  getSite: (siteId: number) => Promise<void>;
  createSite: (siteData: SiteCreation) => Promise<ActionResult>;
  deleteSite: (siteId: number) => Promise<ActionResult>;
  updateSite: (siteId: number, changes: SiteChanges) => Promise<ActionResult>;

  // Site Details actions (from siteManagement)
  loadSiteDetails: (siteId: number) => Promise<void>;
  updateSiteField: <K extends UpdatableSiteField>(
    field: K,
    value: SiteFieldValue<K>
  ) => Promise<ActionResult>;

  // Materials actions
  getMaterialsList: () => Promise<void>;
  getSiteMaterials: (siteId?: number) => Promise<SiteMaterial[]>;
  updateSiteMaterials: (
    changedMaterials: ChangedSiteMaterials[]
  ) => Promise<ActionResult>;
  deleteSiteMaterials: (
    siteId: number,
    materialIds: number[]
  ) => Promise<ActionResult>;
  addMaterialsToSite: (materialCodes: string[]) => Promise<void>;

  // Photos actions
  getSitePhotos: (siteId: number) => Promise<void>;
  getCompressedPhotos: (siteIds: number) => Promise<Photo[] | null>;
  addPhoto: (photoData: NewPhotoData) => Promise<ActionResult>;
  addPhotos: (photosData: NewPhotosData) => Promise<ActionResult>;
  uploadPhoto: (photoData: PhotoUpload) => Promise<void>;
  deletePhoto: (photoId: number, siteId: number) => Promise<ActionResult>;

  // Error actions
  clearErrors: () => void;
  clearSitesError: () => void;
  clearSiteError: () => void;
  clearMaterialsError: () => void;
  clearPhotosError: () => void;

  // Utility actions
  resetUpdateStatuses: () => void;
  clearSiteMaterials: () => void;

  // Legacy compatibility helpers (computed properties)
  isLoading: () => boolean;
  isSitesLoading: () => boolean;
  isMaterialsLoading: () => boolean;
  isPhotosLoading: () => boolean;
}

export const useSitesStore = createStore<SitesStoreState, SitesStoreActions>(
  {
    state: {
      sites: [],
      currentSite: null,
      currentSiteDetails: null,
      updateStatuses: {},
      materials: [],
      siteMaterials: [],
      availableMaterials: [],
      photos: [],
      sitePhotos: [],
      loading: {
        sites: 'idle',
        materials: 'idle',
        photos: 'idle',
      },
      sitesError: null,
      siteError: null,
      materialsError: null,
      photosError: null,
    },
    actions: (set, get) => ({
      // Sites actions - NO try/catch needed, centralized API handles errors
      getSites: async (projectId: number) => {
        set(state => ({
          loading: { ...state.loading, sites: 'fetching' },
          sitesError: null,
        }));

        const response = await sitesApi.getSites(projectId);

        if (response.error || !response.isSuccess) {
          set(state => ({
            sites: [],
            loading: { ...state.loading, sites: 'error' },
            sitesError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch sites',
          }));
          return;
        }

        set(state => ({
          sites: response.result.data || [],
          loading: { ...state.loading, sites: 'idle' },
          sitesError: null,
        }));
      },

      getSite: async (siteId: number) => {
        set(state => ({
          loading: { ...state.loading, sites: 'fetching' },
          siteError: null,
        }));

        const response = await sitesApi.getSite(siteId);

        if (response.error || !response.isSuccess) {
          set(state => ({
            loading: { ...state.loading, sites: 'error' },
            siteError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch site',
          }));
          return;
        }

        set(state => ({
          currentSite: response.result.data,
          loading: { ...state.loading, sites: 'idle' },
          siteError: null,
        }));
      },

      createSite: async (siteData: SiteCreation) => {
        set(state => ({
          loading: { ...state.loading, sites: 'creating' },
          sitesError: null,
        }));

        const response = await sitesApi.createSite(siteData);

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to create site';
          set(state => ({
            sitesError: errorMsg,
            loading: { ...state.loading, sites: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Success case
        set(state => ({
          sites: [...get().sites, response.result],
          loading: { ...state.loading, sites: 'idle' },
          sitesError: null,
        }));

        return { success: true, data: response.result };
      },

      deleteSite: async (siteId: number) => {
        set(state => ({
          loading: { ...state.loading, sites: 'deleting' },
          sitesError: null,
        }));

        const response = await sitesApi.deleteSite(siteId);

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to delete site';
          set(state => ({
            sitesError: errorMsg,
            loading: { ...state.loading, sites: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Success case
        set(state => ({
          sites: get().sites.filter(site => site.id !== siteId),
          loading: { ...state.loading, sites: 'idle' },
          sitesError: null,
        }));

        return { success: true };
      },

      updateSite: async (siteId: number, changes: SiteChanges) => {
        set(state => ({
          loading: { ...state.loading, sites: 'updating' },
          sitesError: null,
        }));

        const response = await sitesApi.updateSite(siteId, changes);

        if (response.error || !response.isSuccess || !response.result.data) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to update site';
          set(state => ({
            sitesError: errorMsg,
            loading: { ...state.loading, sites: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Success case - Update site in existing list instead of full reload
        set(state => ({
          sites: get().sites.map(site =>
            site.id === siteId ? response.result.data : site
          ),
          loading: { ...state.loading, sites: 'idle' },
          sitesError: null,
        }));

        return { success: true, data: response.result.data };
      },

      // Materials actions
      getMaterialsList: async () => {
        set(state => ({
          loading: { ...state.loading, materials: 'fetching' },
          materialsError: null,
        }));

        const response = await sitesApi.getMaterialsList();

        if (response.error || !response.isSuccess) {
          set(state => ({
            loading: { ...state.loading, materials: 'error' },
            materialsError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch materials',
          }));
          return;
        }

        set(state => ({
          materials: response.result.data || [],
          loading: { ...state.loading, materials: 'idle' },
          materialsError: null,
        }));
      },

      getSiteMaterials: async (siteId?: number) => {
        set(state => ({
          loading: { ...state.loading, materials: 'fetching' },
          materialsError: null,
        }));

        const response = await sitesApi.getSiteMaterials(siteId);

        if (response.error || !response.isSuccess) {
          set(state => ({
            loading: { ...state.loading, materials: 'error' },
            materialsError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch site materials',
          }));
          return [];
        }

        set(state => ({
          siteMaterials: response.result.data || [],
          loading: { ...state.loading, materials: 'idle' },
          materialsError: null,
        }));
        return response.result.data || [];
      },

      updateSiteMaterials: async (changedMaterials: ChangedSiteMaterials[]) => {
        set(state => ({
          loading: { ...state.loading, materials: 'updating' },
          materialsError: null,
        }));

        const response = await sitesApi.updateSiteMaterials(changedMaterials);

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to update materials';
          set(state => ({
            materialsError: errorMsg,
            loading: { ...state.loading, materials: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Refresh site materials after update
        const siteId =
          changedMaterials.length > 0 ? changedMaterials[0].siteId : undefined;
        const store = get();
        await store.actions.getSiteMaterials(siteId);
        return { success: true };
      },

      deleteSiteMaterials: async (siteId: number, materialIds: number[]) => {
        set(state => ({
          loading: { ...state.loading, materials: 'deleting' },
          materialsError: null,
        }));

        const response = await sitesApi.deleteSiteMaterials(
          siteId,
          materialIds
        );

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to delete materials';
          set(state => ({
            materialsError: errorMsg,
            loading: { ...state.loading, materials: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Refresh site materials after deletion
        const store = get();
        await store.actions.getSiteMaterials(siteId);
        return { success: true };
      },

      // Photos actions
      getSitePhotos: async (siteId: number) => {
        set(state => ({
          loading: { ...state.loading, photos: 'fetching' },
          photosError: null,
        }));

        const response = await sitesApi.getSitePhotos(siteId);

        if (response.error || !response.isSuccess) {
          set(state => ({
            loading: { ...state.loading, photos: 'error' },
            photosError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch photos',
          }));
          return;
        }

        set(state => ({
          photos: response.result.data || [],
          loading: { ...state.loading, photos: 'idle' },
          photosError: null,
        }));
      },

      getCompressedPhotos: async (siteId: number) => {
        set(state => ({
          loading: { ...state.loading, photos: 'fetching' },
          photosError: null,
        }));

        const response = await sitesApi.getCompressedPhotos(siteId);

        if (response.error || !response.isSuccess) {
          set(state => ({
            loading: { ...state.loading, photos: 'error' },
            photosError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to fetch compressed photos',
          }));
          return null;
        }

        set(state => ({
          photos: response.result.data || [],
          loading: { ...state.loading, photos: 'idle' },
          photosError: null,
        }));
        return response.result.data || [];
      },

      addPhoto: async (photoData: NewPhotoData) => {
        set(state => ({
          loading: { ...state.loading, photos: 'uploading' },
          photosError: null,
        }));

        const response = await sitesApi.addPhoto(photoData);

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to upload photo';
          set(state => ({
            photosError: errorMsg,
            loading: { ...state.loading, photos: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Push new photo to existing photos array instead of refreshing the list
        const currentPhotos = get().photos;
        const newPhoto: Photo = {
          id: response.result.data.id || Date.now(),
          siteId: photoData.siteId,
          base64Image: response.result.data.base64Image,
        };

        set(state => ({
          photos: [...currentPhotos, newPhoto],
          loading: { ...state.loading, photos: 'idle' },
          photosError: null,
        }));
        return { success: true, data: newPhoto };
      },

      addPhotos: async (photosData: NewPhotosData) => {
        set(state => ({
          loading: { ...state.loading, photos: 'uploading' },
          photosError: null,
        }));

        const response = await sitesApi.addPhotos(photosData);

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to upload photos';
          set(state => ({
            photosError: errorMsg,
            loading: { ...state.loading, photos: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Refresh photos after addition
        const store = get();
        await store.actions.getSitePhotos(photosData.siteId);
        return { success: true };
      },

      deletePhoto: async (photoId: number, siteId: number) => {
        set(state => ({
          loading: { ...state.loading, photos: 'deleting' },
          photosError: null,
        }));

        const response = await sitesApi.deletePhoto(photoId, siteId);

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Failed to delete photo';
          set(state => ({
            photosError: errorMsg,
            loading: { ...state.loading, photos: 'error' },
          }));
          return { success: false, error: errorMsg };
        }

        // Refresh photos after deletion
        const store = get();
        await store.actions.getSitePhotos(siteId);
        return { success: true };
      },

      // Error actions
      clearErrors: () => {
        set(state => {
          state.sitesError = null;
          state.siteError = null;
          state.materialsError = null;
          state.photosError = null;
        });
      },

      clearSitesError: () => {
        set(state => {
          state.sitesError = null;
        });
      },

      clearSiteError: () => {
        set(state => {
          state.siteError = null;
        });
      },

      clearMaterialsError: () => {
        set(state => {
          state.materialsError = null;
        });
      },

      clearPhotosError: () => {
        set(state => {
          state.photosError = null;
        });
      },

      // Site Details actions (merged from siteManagementStore)
      loadSiteDetails: async (siteId: number) => {
        set(state => ({
          loading: { ...state.loading, sites: 'fetching' },
          siteError: null,
        }));

        const response = await sitesApi.getSite(siteId);

        if (response.error || !response.isSuccess) {
          set(state => ({
            loading: { ...state.loading, sites: 'error' },
            siteError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to load site details',
          }));
          return;
        }

        // Transform the site data to match our interface
        const siteData = response.result.data;
        const siteDetails: SiteDetails = {
          id: siteData.id,
          projectId: siteData.projectId ?? 0,
          siteName: siteData.siteName,
          priority: siteData.priority ?? 0,
          currentState: siteData.currentState,
          coordinates: {
            latitude: siteData.latitude || 0,
            longitude: siteData.longitude || 0,
          },
          description: siteData.description || '',
          requiredChanges: siteData.requiredChanges || '',
          additionalWorks: siteData.additionalWorks || '',
          createdAt: siteData.creationDate,
          updatedAt: siteData.creationDate,
        };

        set(state => ({
          currentSiteDetails: siteDetails,
          loading: { ...state.loading, sites: 'idle' },
          siteError: null,
        }));
      },

      updateSiteField: async <K extends UpdatableSiteField>(
        field: K,
        value: SiteFieldValue<K>
      ): Promise<ActionResult> => {
        const currentSiteDetails = get().currentSiteDetails;
        if (!currentSiteDetails) {
          return { success: false, error: 'No site details available' };
        }

        // Type-safe validation
        if (!isUpdatableField(field)) {
          const error = `Field '${field}' is read-only and cannot be updated`;
          if (import.meta.env.DEV) {
            console.warn(error);
          }
          return { success: false, error };
        }

        if (!validateFieldValue(field, value)) {
          const error = `Invalid value for field '${field}': ${JSON.stringify(value)}`;
          if (import.meta.env.DEV) {
            console.error(error);
          }
          return { success: false, error };
        }

        // Set updating status
        set(state => {
          state.updateStatuses[field] = { field, status: 'updating' };
        });

        // Create type-safe update data
        const updateData = createSiteFieldUpdate(field, value);

        const response = await sitesApi.updateSite(
          currentSiteDetails.id,
          updateData as SiteChanges
        );

        if (response.error || !response.isSuccess) {
          const errorMsg = response.error
            ? getErrorMessage(response.error)
            : 'Update failed';
          set(state => {
            state.updateStatuses[field] = {
              field,
              status: 'error',
              error: errorMsg,
            };
          });
          return { success: false, error: errorMsg };
        }

        // Update local state with new value
        set(state => {
          state.currentSiteDetails = { ...currentSiteDetails, [field]: value };
          state.updateStatuses[field] = { field, status: 'success' };
        });

        return {
          success: true,
          data: { ...currentSiteDetails, [field]: value },
        };
      },

      addMaterialsToSite: async () => {
        set(state => ({
          loading: { ...state.loading, materials: 'updating' },
          materialsError: null,
        }));

        // This method seems to be a placeholder
        set(state => ({
          loading: { ...state.loading, materials: 'idle' },
        }));
      },

      uploadPhoto: async (photoData: PhotoUpload) => {
        set(state => ({
          loading: { ...state.loading, photos: 'uploading' },
          photosError: null,
        }));

        const response = await sitesApi.addPhoto({
          siteId: photoData.siteId,
          photoData: photoData.photoData,
        });

        if (response.error || !response.isSuccess) {
          set(state => ({
            photosError: response.error
              ? getErrorMessage(response.error)
              : 'Failed to upload photo',
            loading: { ...state.loading, photos: 'error' },
          }));
          return;
        }

        set(state => ({
          loading: { ...state.loading, photos: 'idle' },
          photosError: null,
        }));
      },

      // Utility actions
      resetUpdateStatuses: () => {
        set(state => {
          state.updateStatuses = {};
        });
      },

      clearSiteMaterials: () => {
        set(state => {
          state.siteMaterials = [];
          state.availableMaterials = [];
        });
      },

      // Legacy compatibility helpers (computed properties)
      isLoading: () => {
        const { loading } = get();
        return (
          loading.sites !== 'idle' ||
          loading.materials !== 'idle' ||
          loading.photos !== 'idle'
        );
      },

      isSitesLoading: () => {
        const { loading } = get();
        return loading.sites !== 'idle';
      },

      isMaterialsLoading: () => {
        const { loading } = get();
        return loading.materials !== 'idle';
      },

      isPhotosLoading: () => {
        const { loading } = get();
        return loading.photos !== 'idle';
      },
    }),
  },
  'SitesStore'
);
